import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import Container from '@mui/material/Container';

import Button from '@mui/material/Button';
import MailOutline from '@mui/icons-material/MailOutline';
import Copyright from '../../components/Copyright';
import { useNotification } from '../../context/NotificationContext';
import {
  WEBSITE_NAME,
  SUPPORT_EMAIL,
  WEBSITE_LINK,
  PRODUCT_NAME,
} from '../../common/AppConstants';

function preventDefault(event) {
  event.preventDefault();
}

const termsList = [
  {
    title: 'Cancellation Policy',
    content:
      'You may cancel your subscription at any time from your account settings. Cancellation will stop future auto-renewals but will not end your current subscription period. Your access will remain active until the end of the paid billing cycle. For example, if you have an annual subscription, it will stay active until the completion of those 12 months.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Effect of Cancellation',
    content:
      'Cancelling your subscription does not delete your {PRODUCT_NAME} account. You may re-subscribe at any time to regain full access to premium features. If you choose to delete your account, please submit a separate request to our support team.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Refunds',
    content:
      'As {PRODUCT_NAME} provides access to digital content and services, all payments are generally non-refundable once a subscription period begins. However, refunds may be issued in certain exceptional cases such as duplicate payments, technical errors, or other verified billing issues. To request a refund, please contact {SUPPORT_EMAIL} within 7 days of the transaction. Refund eligibility will be determined at our sole discretion.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Subscription Renewals',
    content:
      'All subscriptions are set to auto-renew at the end of each billing cycle to ensure uninterrupted access to your plan features. Renewal charges are automatically processed using your selected payment method unless you cancel the subscription before the renewal date.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Trial Periods and Promotional Offers',
    content:
      'If you cancel during a free trial, you will not be charged. For promotional or discounted subscriptions, standard renewal rates apply after the offer period unless cancelled before renewal.',
    children: [
      {
        content: '',
        listItems: [],
      },
    ],
  },
  {
    title: 'Changes to This Refund Policy',
    content:
      'We reserve the right to update this Refund Policy at any time. Users are responsible for reviewing them regularly. Continued use of the service after changes means you accept them.',
    children: [
      {
        content:
          'We will notify users of significant changes via email or website notifications.',
        listItems: [],
      },
    ],
  },
  {
    title: 'Contact Us',
    content:
      'For any questions or concerns regarding this Refund Policy, please contact us at {SUPPORT_EMAIL}.',
    children: [
      {
        content: 'For any issues related to billing, cancellation, or refund requests, please reach out to us at {SUPPORT_EMAIL}. Our team will review your concern and respond promptly.',
        listItems: [],
      },
    ],
  },
];

const replacePlaceholders = (text) => {
  if (!text) return text; // Handle empty strings gracefully
  return text
    .replaceAll('{SUPPORT_EMAIL}', SUPPORT_EMAIL)
    .replaceAll('{WEBSITE_LINK}', WEBSITE_LINK)
    .replaceAll('{WEBSITE_NAME}', WEBSITE_NAME)
    .replaceAll('{PRODUCT_NAME}', PRODUCT_NAME);
};

export default function RefundPolicy() {
  const { addNotification } = useNotification();

  return (
    <Container component="main" maxWidth="md" sx={{ py: 5 }}>
      <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        Cancellation & Refund Policy
      </Typography>
      <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 5 }}>
        At {PRODUCT_NAME} [{WEBSITE_NAME}], we strive to ensure that our users have a seamless and transparent experience.
        This policy outlines the terms governing cancellations, renewals, and refunds for all subscriptions and payments made through our platform.
      </Typography>

      {termsList.map((section, section_index) => (
        <Box key={section_index}>
          <Typography variant="h5" sx={{ mt: 3 }}>
            {section_index + 1}
            {'. '}
            {replacePlaceholders(section.title)}
          </Typography>
          <Typography variant="body1">
            {replacePlaceholders(section.content)}
          </Typography>

          {section.children.map((children, children_index) => (
            <Box key={section_index}>
              <Typography variant="body1" sx={{ pt: 1, pb: 0, pl: 0, pr: 0 }}>
                {replacePlaceholders(children.content)}
              </Typography>
              <List disablePadding>
                {children.listItems.map((item, item_index) => (
                  <ListItem disablePadding key={item_index} sx={{ pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {'* '}
                          {replacePlaceholders(item)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      ))}

      <Typography variant="h5" sx={{ mt: 3 }}>
        Last Updated: 24th October, 2025
      </Typography>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
